import { company } from "./company";

export type PrintableDebtReconciliation = {
  number: string;
  debtType: "Receivable" | "Payable";
  partnerCode: string;
  partnerName: string;
  partnerTaxCode?: string | null;
  partnerAddress?: string | null;
  periodFrom: string;
  periodTo: string;
  openingBalance: number;
  increaseAmount: number;
  decreaseAmount: number;
  internalClosingBalance: number;
  partnerConfirmedBalance?: number | null;
  difference: number;
  notes?: string | null;
  lines: Array<{
    lineNo: number;
    entryDate: string;
    sourceNumber: string;
    description: string;
    increaseAmount: number;
    decreaseAmount: number;
    runningBalance: number;
  }>;
};

const escapeHtml = (value: unknown) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
}[char] ?? char));
const formatDate = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString("vi-VN");
const money = (value: number) => value.toLocaleString("vi-VN", { maximumFractionDigits: 0 });

export function printDebtReconciliation(row: PrintableDebtReconciliation, saveAsPdf = false) {
  const popup = window.open("", "_blank", "width=1120,height=800");
  if (!popup) return;
  const balanceLabel = row.debtType === "Receivable" ? "còn phải thu" : "còn phải trả";
  const lineRows = row.lines.map((line) => `<tr>
    <td>${line.lineNo}</td><td>${escapeHtml(formatDate(line.entryDate))}</td>
    <td>${escapeHtml(line.sourceNumber)}</td><td>${escapeHtml(line.description)}</td>
    <td class="num">${line.increaseAmount ? money(line.increaseAmount) : "—"}</td>
    <td class="num">${line.decreaseAmount ? money(line.decreaseAmount) : "—"}</td>
    <td class="num"><strong>${money(line.runningBalance)}</strong></td>
  </tr>`).join("");
  popup.document.write(`<!doctype html><html><head><title>Đối chiếu công nợ ${escapeHtml(row.number)}</title><style>
    @page{size:A4 landscape;margin:10mm}*{box-sizing:border-box}body{font-family:"Times New Roman",serif;color:#111;font-size:12px;margin:0}
    .top{display:grid;grid-template-columns:1fr 1fr;gap:25px}.company strong{display:block;font-size:14px}.company p,.partner p{margin:2px 0}
    h1{text-align:center;font-size:21px;margin:18px 0 2px}.number{text-align:center;margin-bottom:14px}.period{text-align:center;font-style:italic;margin-bottom:16px}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid #111;margin:12px 0}.summary div{padding:8px;border-right:1px solid #111}.summary div:last-child{border-right:0}.summary span{display:block}.summary strong{display:block;text-align:right;font-size:14px;margin-top:5px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #111;padding:5px 6px;vertical-align:top}th{text-align:center;background:#f3f3f3}.num{text-align:right;white-space:nowrap}
    .confirm{margin-top:12px;border:1px solid #111;padding:9px}.confirm p{margin:3px 0}.difference{font-weight:700}.signatures{display:grid;grid-template-columns:1fr 1fr;gap:80px;text-align:center;margin-top:25px}.signatures strong{display:block;margin-bottom:55px}.hint{text-align:center;color:#666;margin-top:20px}
  </style></head><body>
    <div class="top"><div class="company"><strong>${escapeHtml(company.legalName)}</strong><p>${escapeHtml(company.address)}</p><p>MST: ${escapeHtml(company.taxCode)} · ĐT: ${escapeHtml(company.phone)}</p></div>
    <div class="partner"><strong>ĐỐI TÁC: ${escapeHtml(row.partnerName)}</strong><p>Mã: ${escapeHtml(row.partnerCode)} · MST: ${escapeHtml(row.partnerTaxCode || "—")}</p><p>Địa chỉ: ${escapeHtml(row.partnerAddress || "—")}</p></div></div>
    <h1>BIÊN BẢN ĐỐI CHIẾU CÔNG NỢ</h1><div class="number">Số: <strong>${escapeHtml(row.number)}</strong></div>
    <div class="period">Từ ngày ${escapeHtml(formatDate(row.periodFrom))} đến ngày ${escapeHtml(formatDate(row.periodTo))}</div>
    <div class="summary"><div><span>Số dư đầu kỳ</span><strong>${money(row.openingBalance)}</strong></div><div><span>Phát sinh tăng</span><strong>${money(row.increaseAmount)}</strong></div><div><span>Phát sinh giảm</span><strong>${money(row.decreaseAmount)}</strong></div><div><span>Cuối kỳ ${balanceLabel}</span><strong>${money(row.internalClosingBalance)}</strong></div></div>
    <table><thead><tr><th>STT</th><th>Ngày</th><th>Số chứng từ</th><th>Nội dung</th><th>Phát sinh tăng</th><th>Phát sinh giảm</th><th>Số dư</th></tr></thead><tbody>${lineRows || '<tr><td colspan="7" style="text-align:center">Không có phát sinh trong kỳ.</td></tr>'}</tbody></table>
    <div class="confirm"><p>Số dư theo Gia Bảo: <strong>${money(row.internalClosingBalance)} đồng</strong>.</p><p>Số dư đối tác xác nhận: <strong>${row.partnerConfirmedBalance == null ? "Chưa xác nhận" : `${money(row.partnerConfirmedBalance)} đồng`}</strong>.</p><p class="difference">Chênh lệch: ${money(row.difference)} đồng.</p>${row.notes ? `<p>Ghi chú/xử lý: ${escapeHtml(row.notes)}</p>` : ""}</div>
    <div class="signatures"><div><strong>ĐẠI DIỆN ${escapeHtml(company.brandName)}</strong>(Ký, ghi rõ họ tên)</div><div><strong>ĐẠI DIỆN ${escapeHtml(row.partnerName)}</strong>(Ký, ghi rõ họ tên)</div></div>
    ${saveAsPdf ? '<p class="hint">Trong cửa sổ in, chọn “Save as PDF / Lưu dưới dạng PDF”.</p>' : ""}
    <script>window.onload=()=>window.print();</script></body></html>`);
  popup.document.close();
}
